/**
 * @fileoverview ClubOS Youth Academy — Fee Management
 * Monthly fee tracking: Paid / Partial / Due, partial payments, discounts.
 * Audit columns (createdBy, createdAt, updatedBy, updatedAt) are included
 * in the PDF report only — not shown in the UI table.
 * Per-player history dialog with edit support (Item 18).
 */

import { useState, useEffect, useCallback } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import Divider from "@mui/material/Divider";
import Alert from "@mui/material/Alert";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftBadge from "components/SoftBadge";
import SoftButton from "components/SoftButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import MiniStatisticsCard from "examples/Cards/StatisticsCards/MiniStatisticsCard";

import PropTypes from "prop-types";
import { academyService } from "api";
import FootballLoader from "components/FootballLoader";

const STATUS_COLOR  = { Paid: "success", Partial: "warning", Due: "error", Waived: "info" };
const MONTHS        = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const PAYMENT_MODES = ["Cash", "UPI", "Online", "Cheque", "NEFT"];
const FEE_STATUSES  = ["Paid", "Partial", "Due", "Waived"];
const currentYear   = new Date().getFullYear();
const currentMonth  = new Date().getMonth() + 1;

const emptyPayForm = {
  amount: "", paymentMode: "UPI",
  paymentDate: new Date().toISOString().slice(0, 10),
  transactionRef: "", notes: "",
};

const emptyEditForm = {
  totalAmount: "", paidAmount: "", feeStatus: "Due", paymentMode: "UPI",
  paymentDate: new Date().toISOString().slice(0, 10),
  transactionRef: "", notes: "",
};

// ── PDF report (includes audit columns) ───────────────────────────────────────
async function downloadFeesPDF(fees, filterStatus, filterMonth, filterYear) {
  const { jsPDF }  = await import("jspdf");
  const autoTable   = (await import("jspdf-autotable")).default;

  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
  const now = new Date().toLocaleString("en-IN");
  const monthLabel = filterMonth ? MONTHS[filterMonth - 1] : "All Months";

  doc.setFontSize(16);
  doc.text("Fee Collection Report", 14, 18);
  doc.setFontSize(10);
  doc.setTextColor(100);
  doc.text(`Generated: ${now}`, 14, 25);
  doc.text(`Period: ${monthLabel} ${filterYear}  |  Status: ${filterStatus || "All"}  |  Total records: ${fees.length}`, 14, 30);

  const totalFees      = fees.reduce((s, f) => s + Number(f.totalAmount), 0);
  const totalCollected = fees.reduce((s, f) => s + Number(f.paidAmount), 0);
  const totalPending   = fees.reduce((s, f) => s + Number(f.outstandingBalance ?? 0), 0);
  const pct = totalFees > 0 ? Math.round(totalCollected / totalFees * 100) : 0;
  doc.setTextColor(0);
  doc.setFontSize(11);
  doc.text(
    `Total: ₹${totalFees.toLocaleString("en-IN")}  |  Collected: ₹${totalCollected.toLocaleString("en-IN")} (${pct}%)  |  Outstanding: ₹${totalPending.toLocaleString("en-IN")}`,
    14, 36,
  );

  const fmt = (v) => v ? new Date(v).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

  autoTable(doc, {
    startY: 42,
    head: [[
      "#", "Student", "Batch", "Month/Year", "Total (₹)", "Paid (₹)", "Outstanding (₹)",
      "Status", "Mode", "Txn Ref",
      "Recorded By", "Recorded On", "Last Updated By", "Last Updated On",
    ]],
    body: fees.map((f, i) => [
      i + 1,
      f.playerName ?? "",
      f.batchName  ?? "—",
      `${MONTHS[(f.feeMonth ?? 1) - 1]} ${f.feeYear}`,
      Number(f.totalAmount).toLocaleString("en-IN"),
      Number(f.paidAmount).toLocaleString("en-IN"),
      Number(f.outstandingBalance ?? 0).toLocaleString("en-IN"),
      f.feeStatus   ?? "",
      f.paymentMode ?? "—",
      f.transactionRef ?? "—",
      f.createdBy   ?? f.CreatedBy   ?? "—",
      fmt(f.createdAt  ?? f.CreatedAt),
      f.updatedBy   ?? f.UpdatedBy   ?? "—",
      fmt(f.updatedAt  ?? f.UpdatedAt),
    ]),
    styles: { fontSize: 7, cellPadding: 1.5 },
    headStyles: { fillColor: [26, 115, 232], textColor: 255, fontStyle: "bold", fontSize: 7 },
    alternateRowStyles: { fillColor: [245, 248, 255] },
    columnStyles: {
      10: { textColor: [120, 120, 120] },
      11: { textColor: [120, 120, 120] },
      12: { textColor: [120, 120, 120] },
      13: { textColor: [120, 120, 120] },
    },
  });

  const finalY = doc.lastAutoTable.finalY + 6;
  doc.setFontSize(8);
  doc.setTextColor(150);
  doc.text("Columns 11–14 contain audit trail data (Recorded By / On, Last Updated By / On). This report is confidential.", 14, finalY);

  doc.save(`fees-report-${filterYear}-${String(filterMonth).padStart(2, "0")}-${new Date().toISOString().slice(0, 10)}.pdf`);
}

// ── Per-player history PDF ────────────────────────────────────────────────────
async function downloadPlayerHistoryPDF(playerName, history) {
  const { jsPDF }  = await import("jspdf");
  const autoTable   = (await import("jspdf-autotable")).default;

  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
  const now = new Date().toLocaleString("en-IN");

  doc.setFontSize(16);
  doc.text(`Fee History — ${playerName}`, 14, 18);
  doc.setFontSize(10);
  doc.setTextColor(100);
  doc.text(`Generated: ${now}  |  Total records: ${history.length}`, 14, 25);

  const fmt = (v) => v ? new Date(v).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

  autoTable(doc, {
    startY: 32,
    head: [[
      "Month/Year", "Total (₹)", "Paid (₹)", "Outstanding (₹)", "Status",
      "Mode", "Txn Ref", "Notes",
      "Recorded By", "Recorded On", "Last Updated By", "Last Updated On",
    ]],
    body: history.map((f) => [
      `${MONTHS[(f.feeMonth ?? 1) - 1]} ${f.feeYear}`,
      Number(f.totalAmount).toLocaleString("en-IN"),
      Number(f.paidAmount).toLocaleString("en-IN"),
      Number(f.outstandingBalance ?? 0).toLocaleString("en-IN"),
      f.feeStatus ?? "",
      f.paymentMode ?? "—",
      f.transactionRef ?? "—",
      f.notes ?? "—",
      f.createdBy  ?? f.CreatedBy  ?? "—",
      fmt(f.createdAt ?? f.CreatedAt),
      f.updatedBy  ?? f.UpdatedBy  ?? "—",
      fmt(f.updatedAt ?? f.UpdatedAt),
    ]),
    styles: { fontSize: 7, cellPadding: 1.5 },
    headStyles: { fillColor: [26, 115, 232], textColor: 255, fontStyle: "bold", fontSize: 7 },
    alternateRowStyles: { fillColor: [245, 248, 255] },
    columnStyles: {
      8:  { textColor: [120, 120, 120] },
      9:  { textColor: [120, 120, 120] },
      10: { textColor: [120, 120, 120] },
      11: { textColor: [120, 120, 120] },
    },
  });

  const finalY = doc.lastAutoTable.finalY + 6;
  doc.setFontSize(8);
  doc.setTextColor(150);
  doc.text("Columns 9–12 contain audit trail data. This report is confidential.", 14, finalY);

  const safeName = playerName.replace(/[^a-z0-9]/gi, "_").toLowerCase();
  doc.save(`fee-history-${safeName}-${new Date().toISOString().slice(0, 10)}.pdf`);
}

// ── Per-player history dialog ─────────────────────────────────────────────────
function PlayerHistoryDialog({ open, onClose, player, onEdit }) {
  const [history, setHistory]     = useState([]);
  const [loading, setLoading]     = useState(false);

  useEffect(() => {
    if (!open || !player) return;
    setLoading(true);
    academyService.getFees({ playerId: player.playerId })
      .then((d) => setHistory(Array.isArray(d) ? [...d].sort((a, b) => {
        if (b.feeYear !== a.feeYear) return b.feeYear - a.feeYear;
        return (b.feeMonth ?? 0) - (a.feeMonth ?? 0);
      }) : []))
      .catch(() => setHistory([]))
      .finally(() => setLoading(false));
  }, [open, player]);

  const totalPaid = history.reduce((s, f) => s + Number(f.paidAmount), 0);
  const totalDue  = history.reduce((s, f) => s + Number(f.outstandingBalance ?? 0), 0);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { maxHeight: "85vh" } }}>
      <DialogTitle>
        <SoftBox display="flex" justifyContent="space-between" alignItems="center">
          <SoftBox>
            <SoftTypography variant="h6" fontWeight="bold" color="dark">{player?.playerName}</SoftTypography>
            <SoftTypography variant="caption" color="text">{player?.batchName ?? "No batch"} · Fee History</SoftTypography>
          </SoftBox>
          <IconButton size="small" onClick={onClose}><Icon>close</Icon></IconButton>
        </SoftBox>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        {/* Summary strip */}
        <SoftBox display="flex" gap={3} px={2.5} py={1.5} sx={{ backgroundColor: "#fafbff", borderBottom: "1px solid #f0f0f0" }}>
          <SoftBox>
            <SoftTypography variant="caption" color="text">Total Paid</SoftTypography>
            <SoftTypography variant="h6" fontWeight="bold" sx={{ color: "#2e7d32" }}>
              ₹{totalPaid.toLocaleString("en-IN")}
            </SoftTypography>
          </SoftBox>
          <Divider orientation="vertical" flexItem />
          <SoftBox>
            <SoftTypography variant="caption" color="text">Outstanding</SoftTypography>
            <SoftTypography variant="h6" fontWeight="bold" sx={{ color: totalDue > 0 ? "#d32f2f" : "#2e7d32" }}>
              ₹{totalDue.toLocaleString("en-IN")}
            </SoftTypography>
          </SoftBox>
          <Divider orientation="vertical" flexItem />
          <SoftBox>
            <SoftTypography variant="caption" color="text">Records</SoftTypography>
            <SoftTypography variant="h6" fontWeight="bold" color="dark">{history.length}</SoftTypography>
          </SoftBox>
        </SoftBox>

        {/* Column header */}
        <SoftBox display="flex" alignItems="center" px={2.5} py={1}
          sx={{ borderBottom: "1px solid #f0f0f0", backgroundColor: "#f7f8fa" }}>
          <SoftTypography variant="caption" fontWeight="bold" color="text" sx={{ flex: "0 0 130px" }}>MONTH</SoftTypography>
          <SoftTypography variant="caption" fontWeight="bold" color="text" sx={{ flex: "0 0 90px", textAlign: "right" }}>TOTAL</SoftTypography>
          <SoftTypography variant="caption" fontWeight="bold" color="text" sx={{ flex: "0 0 90px", textAlign: "right" }}>PAID</SoftTypography>
          <SoftTypography variant="caption" fontWeight="bold" color="text" sx={{ flex: "0 0 90px", textAlign: "right" }}>DUE</SoftTypography>
          <SoftTypography variant="caption" fontWeight="bold" color="text" sx={{ flex: "0 0 80px", textAlign: "center" }}>STATUS</SoftTypography>
          <SoftTypography variant="caption" fontWeight="bold" color="text" sx={{ flex: 1 }}>MODE / REF</SoftTypography>
          <SoftBox sx={{ width: 40 }} />
        </SoftBox>

        {loading ? (
          <FootballLoader label="Loading history…" />
        ) : history.length === 0 ? (
          <SoftBox p={4} textAlign="center">
            <SoftTypography variant="caption" color="text" sx={{ fontStyle: "italic" }}>No records found</SoftTypography>
          </SoftBox>
        ) : (
          history.map((f) => (
            <SoftBox key={f.id} display="flex" alignItems="center" px={2.5} py={1.25}
              sx={{ borderBottom: "1px solid #f9f9f9", "&:hover": { backgroundColor: "rgba(26,115,232,0.02)" } }}>
              <SoftTypography variant="caption" fontWeight="medium" color="dark" sx={{ flex: "0 0 130px" }}>
                {MONTHS[(f.feeMonth ?? 1) - 1]} {f.feeYear}
              </SoftTypography>
              <SoftTypography variant="caption" color="dark" sx={{ flex: "0 0 90px", textAlign: "right" }}>
                ₹{Number(f.totalAmount).toLocaleString("en-IN")}
              </SoftTypography>
              <SoftTypography variant="caption" sx={{ flex: "0 0 90px", textAlign: "right", color: "#2e7d32" }}>
                ₹{Number(f.paidAmount).toLocaleString("en-IN")}
              </SoftTypography>
              <SoftTypography variant="caption" fontWeight="bold"
                sx={{ flex: "0 0 90px", textAlign: "right", color: Number(f.outstandingBalance) > 0 ? "#d32f2f" : "#2e7d32" }}>
                ₹{Number(f.outstandingBalance ?? 0).toLocaleString("en-IN")}
              </SoftTypography>
              <SoftBox sx={{ flex: "0 0 80px", textAlign: "center" }}>
                <SoftBadge badgeContent={f.feeStatus} color={STATUS_COLOR[f.feeStatus] ?? "info"} size="xs" container />
              </SoftBox>
              <SoftBox sx={{ flex: 1 }}>
                <SoftTypography variant="caption" color="text">
                  {f.paymentMode ?? "—"}
                  {f.transactionRef ? ` · ${f.transactionRef}` : ""}
                </SoftTypography>
              </SoftBox>
              <IconButton size="small" onClick={() => onEdit(f)} sx={{ width: 28, height: 28 }}>
                <Icon sx={{ fontSize: "0.9rem", color: "#1a73e8" }}>edit</Icon>
              </IconButton>
            </SoftBox>
          ))
        )}
      </DialogContent>
      <DialogActions sx={{ px: 2.5, py: 1.5 }}>
        <SoftButton variant="outlined" color="info" size="small"
          startIcon={<Icon>download</Icon>}
          disabled={history.length === 0}
          onClick={() => downloadPlayerHistoryPDF(player?.playerName ?? "player", history)}>
          Download History PDF
        </SoftButton>
        <SoftButton variant="text" color="secondary" onClick={onClose}>Close</SoftButton>
      </DialogActions>
    </Dialog>
  );
}
PlayerHistoryDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  player: PropTypes.object,
  onEdit: PropTypes.func.isRequired,
};

// ── Edit fee record dialog ────────────────────────────────────────────────────
function EditFeeDialog({ open, onClose, record, onSaved }) {
  const [form, setForm]   = useState(emptyEditForm);
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState("");

  useEffect(() => {
    if (!record) return;
    setForm({
      totalAmount:    String(record.totalAmount ?? ""),
      paidAmount:     String(record.paidAmount ?? ""),
      feeStatus:      record.feeStatus ?? "Due",
      paymentMode:    record.paymentMode ?? "UPI",
      paymentDate:    record.paymentDate ? String(record.paymentDate).slice(0, 10)
                        : new Date().toISOString().slice(0, 10),
      transactionRef: record.transactionRef ?? "",
      notes:          record.notes ?? "",
    });
    setError("");
  }, [record]);

  const f = (key) => (e) => setForm((p) => ({ ...p, [key]: e.target.value }));

  const handleSave = async () => {
    if (!form.totalAmount || isNaN(Number(form.totalAmount))) {
      setError("Total amount must be a valid number."); return;
    }
    setSaving(true); setError("");
    try {
      await academyService.updateFee(record.id, {
        totalAmount:    Number(form.totalAmount),
        paidAmount:     Number(form.paidAmount || 0),
        feeStatus:      form.feeStatus,
        paymentMode:    form.paymentMode,
        paymentDate:    form.paymentDate || null,
        transactionRef: form.transactionRef || null,
        notes:          form.notes || null,
      });
      onSaved();
      onClose();
    } catch (e) {
      setError(e?.detail ?? e?.title ?? "Save failed.");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        <SoftBox>
          <SoftTypography variant="h6" fontWeight="bold" color="dark">Edit Fee Record</SoftTypography>
          {record && (
            <SoftTypography variant="caption" color="text">
              {record.playerName} · {MONTHS[(record.feeMonth ?? 1) - 1]} {record.feeYear}
            </SoftTypography>
          )}
        </SoftBox>
      </DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Alert severity="info" sx={{ mb: 2, py: 0.5 }}>
          <SoftTypography variant="caption">Audit trail (who changed, when) is captured automatically and visible in the PDF report.</SoftTypography>
        </Alert>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextField fullWidth required label="Total Amount (₹)" type="number" value={form.totalAmount}
              onChange={f("totalAmount")} size="small"
              InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }} />
          </Grid>
          <Grid item xs={6}>
            <TextField fullWidth label="Paid Amount (₹)" type="number" value={form.paidAmount}
              onChange={f("paidAmount")} size="small"
              InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }} />
          </Grid>
          <Grid item xs={6}>
            <TextField select fullWidth label="Status" value={form.feeStatus}
              onChange={f("feeStatus")} size="small">
              {FEE_STATUSES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={6}>
            <TextField select fullWidth label="Payment Mode" value={form.paymentMode}
              onChange={f("paymentMode")} size="small">
              {PAYMENT_MODES.map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label="Payment Date" type="date" value={form.paymentDate}
              onChange={f("paymentDate")} size="small" InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label="Transaction Reference" value={form.transactionRef}
              onChange={f("transactionRef")} size="small" />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label="Notes" value={form.notes}
              onChange={f("notes")} size="small" multiline rows={2} />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <SoftButton variant="text" color="secondary" onClick={onClose}>Cancel</SoftButton>
        <SoftButton variant="gradient" color="info" onClick={handleSave} disabled={saving}>
          {saving ? "Saving…" : "Save Changes"}
        </SoftButton>
      </DialogActions>
    </Dialog>
  );
}
EditFeeDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  record: PropTypes.object,
  onSaved: PropTypes.func.isRequired,
};

// ── Fee row ────────────────────────────────────────────────────────────────────
function FeeRow({ record, onPay, onViewHistory, onEdit }) {
  const outstanding = Number(record.outstandingBalance ?? 0);
  return (
    <SoftBox
      display="flex" alignItems="center" py={1.5} px={2}
      sx={{ borderBottom: "1px solid #f5f5f5", "&:hover": { backgroundColor: "rgba(26,115,232,0.02)" } }}
    >
      <SoftBox flex={1}>
        <SoftTypography
          variant="button" fontWeight="medium" color="info" display="block" lineHeight={1.3}
          sx={{ cursor: "pointer", "&:hover": { textDecoration: "underline" } }}
          onClick={() => onViewHistory(record)}
        >
          {record.playerName}
        </SoftTypography>
        <SoftTypography variant="caption" color="text">{record.batchName ?? "No batch"}</SoftTypography>
      </SoftBox>
      <SoftTypography variant="caption" color="text" sx={{ flex: "0 0 120px" }}>
        {MONTHS[(record.feeMonth ?? 1) - 1]} {record.feeYear}
      </SoftTypography>
      <SoftTypography variant="caption" fontWeight="medium" color="dark" sx={{ flex: "0 0 85px", textAlign: "right" }}>
        ₹{Number(record.totalAmount).toLocaleString("en-IN")}
      </SoftTypography>
      <SoftTypography variant="caption" fontWeight="medium" color="success" sx={{ flex: "0 0 85px", textAlign: "right" }}>
        ₹{Number(record.paidAmount).toLocaleString("en-IN")}
      </SoftTypography>
      <SoftTypography
        variant="caption" fontWeight="bold"
        sx={{ flex: "0 0 85px", textAlign: "right", color: outstanding > 0 ? "#d32f2f" : "#2e7d32" }}
      >
        ₹{outstanding.toLocaleString("en-IN")}
      </SoftTypography>
      <SoftBox sx={{ flex: "0 0 80px", textAlign: "center" }}>
        <SoftBadge badgeContent={record.feeStatus} color={STATUS_COLOR[record.feeStatus] ?? "info"} size="xs" container />
      </SoftBox>
      <SoftBox sx={{ flex: "0 0 75px", textAlign: "right", display: "flex", justifyContent: "flex-end", gap: 0.5 }}>
        {record.feeStatus !== "Paid" && record.feeStatus !== "Waived" && (
          <SoftButton variant="text" color="success" iconOnly size="small" onClick={() => onPay(record)}>
            <Icon>payments</Icon>
          </SoftButton>
        )}
        <SoftButton variant="text" color="info" iconOnly size="small" onClick={() => onEdit(record)}>
          <Icon>edit</Icon>
        </SoftButton>
      </SoftBox>
    </SoftBox>
  );
}

FeeRow.propTypes = {
  record: PropTypes.shape({
    playerName: PropTypes.string,
    batchName: PropTypes.string,
    feeMonth: PropTypes.number,
    feeYear: PropTypes.number,
    totalAmount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    paidAmount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    outstandingBalance: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    feeStatus: PropTypes.string,
  }).isRequired,
  onPay: PropTypes.func.isRequired,
  onViewHistory: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
};

// ── Main ───────────────────────────────────────────────────────────────────────
function AcademyFees() {
  const [fees, setFees]               = useState([]);
  const [loading, setLoading]         = useState(true);
  const [filterStatus, setFilterStatus] = useState("");
  const [filterMonth, setFilterMonth] = useState(currentMonth);
  const [filterYear, setFilterYear]   = useState(currentYear);
  const [payDialog, setPayDialog]     = useState(false);
  const [payRecord, setPayRecord]     = useState(null);
  const [payForm, setPayForm]         = useState(emptyPayForm);
  const [saving, setSaving]           = useState(false);

  // Per-player history
  const [historyPlayer, setHistoryPlayer]   = useState(null);
  const [historyDialog, setHistoryDialog]   = useState(false);
  const [editRecord, setEditRecord]         = useState(null);
  const [editDialog, setEditDialog]         = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    academyService.getFees({ status: filterStatus || undefined, month: filterMonth, year: filterYear })
      .then((d) => setFees(Array.isArray(d) ? d : []))
      .catch(() => setFees([]))
      .finally(() => setLoading(false));
  }, [filterStatus, filterMonth, filterYear]);

  useEffect(() => { load(); }, [load]);

  const openPay = (record) => {
    setPayRecord(record);
    setPayForm({ ...emptyPayForm, amount: String(record.outstandingBalance ?? "") });
    setPayDialog(true);
  };

  const openHistory = (record) => {
    setHistoryPlayer(record);
    setHistoryDialog(true);
  };

  const openEdit = (record) => {
    setEditRecord(record);
    setEditDialog(true);
  };

  const handlePay = async () => {
    setSaving(true);
    try {
      await academyService.recordFee({
        playerId:       payRecord.playerId,
        feeMonth:       payRecord.feeMonth,
        feeYear:        payRecord.feeYear,
        totalAmount:    Number(payRecord.totalAmount),
        paidAmount:     Number(payForm.amount),
        discountAmount: Number(payRecord.discountAmount ?? 0),
        paymentMode:    payForm.paymentMode,
        paymentDate:    payForm.paymentDate,
        transactionRef: payForm.transactionRef || null,
        notes:          payForm.notes || null,
        tenantId:       payRecord.tenantId,
      });
      setPayDialog(false);
      load();
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  const totalFees      = fees.reduce((s, f) => s + Number(f.totalAmount), 0);
  const totalCollected = fees.reduce((s, f) => s + Number(f.paidAmount), 0);
  const totalPending   = fees.reduce((s, f) => s + Number(f.outstandingBalance ?? 0), 0);
  const collectionPct  = totalFees > 0 ? Math.round(totalCollected / totalFees * 100) : 0;
  const paid    = fees.filter((f) => f.feeStatus === "Paid").length;
  const partial = fees.filter((f) => f.feeStatus === "Partial").length;
  const due     = fees.filter((f) => f.feeStatus === "Due").length;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3}>

        <SoftBox mb={3} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <SoftBox>
            <SoftTypography variant="h4" fontWeight="bold" color="dark">Fee Management</SoftTypography>
            <SoftTypography variant="body2" color="text">Monthly fee tracking · click a student name to view full history</SoftTypography>
          </SoftBox>
          <SoftButton variant="outlined" color="info" startIcon={<Icon>download</Icon>}
            onClick={() => downloadFeesPDF(fees, filterStatus, filterMonth, filterYear)}
            disabled={fees.length === 0}>
            Download Report
          </SoftButton>
        </SoftBox>

        {/* KPIs */}
        <SoftBox mb={3}>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}>
              <MiniStatisticsCard title={{ text: "Total Fees" }}
                count={`₹${Math.round(totalFees / 1000)}K`}
                percentage={{ color: "info", text: `${fees.length} records` }}
                icon={{ color: "info", component: "receipt" }} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <MiniStatisticsCard title={{ text: "Collected" }}
                count={`₹${Math.round(totalCollected / 1000)}K`}
                percentage={{ color: "success", text: `${collectionPct}% rate` }}
                icon={{ color: "success", component: "payments" }} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <MiniStatisticsCard title={{ text: "Outstanding" }}
                count={`₹${Math.round(totalPending / 1000)}K`}
                percentage={{ color: totalPending > 0 ? "error" : "success", text: `${due} due` }}
                icon={{ color: "error", component: "money_off" }} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <MiniStatisticsCard title={{ text: "Partial" }}
                count={String(partial)}
                percentage={{ color: "warning", text: "partial payments" }}
                icon={{ color: "warning", component: "hourglass_bottom" }} />
            </Grid>
          </Grid>
        </SoftBox>

        {/* Table */}
        <Card>
          {/* Filters */}
          <SoftBox p={2} display="flex" gap={2} flexWrap="wrap" alignItems="center"
            sx={{ borderBottom: "1px solid #f0f0f0" }}>
            <TextField select size="small" label="Month" value={filterMonth}
              onChange={(e) => setFilterMonth(e.target.value)} sx={{ minWidth: 130 }}>
              {MONTHS.map((m, i) => <MenuItem key={i + 1} value={i + 1}>{m}</MenuItem>)}
            </TextField>
            <TextField select size="small" label="Year" value={filterYear}
              onChange={(e) => setFilterYear(e.target.value)} sx={{ minWidth: 90 }}>
              {[currentYear - 2, currentYear - 1, currentYear, currentYear + 1].map((y) => (
                <MenuItem key={y} value={y}>{y}</MenuItem>
              ))}
            </TextField>
            <TextField select size="small" label="◈ Status" value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)} sx={{ minWidth: 120 }}>
              <MenuItem value="">◈ All Status</MenuItem>
              <MenuItem value="Paid">▲ Paid</MenuItem>
              <MenuItem value="Partial">▶ Partial</MenuItem>
              <MenuItem value="Due">▼ Due</MenuItem>
              <MenuItem value="Waived">◆ Waived</MenuItem>
            </TextField>
            <SoftTypography variant="caption" color="text" sx={{ ml: "auto" }}>
              {paid} paid · {partial} partial · {due} due
            </SoftTypography>
          </SoftBox>

          {/* Column headers */}
          <SoftBox display="flex" alignItems="center" px={2} py={1}
            sx={{ borderBottom: "2px solid #f0f0f0", backgroundColor: "#fafafa" }}>
            <SoftTypography variant="caption" fontWeight="bold" color="text" sx={{ flex: 1 }}>STUDENT</SoftTypography>
            <SoftTypography variant="caption" fontWeight="bold" color="text" sx={{ flex: "0 0 120px" }}>MONTH</SoftTypography>
            <SoftTypography variant="caption" fontWeight="bold" color="text" sx={{ flex: "0 0 85px", textAlign: "right" }}>TOTAL</SoftTypography>
            <SoftTypography variant="caption" fontWeight="bold" color="text" sx={{ flex: "0 0 85px", textAlign: "right" }}>PAID</SoftTypography>
            <SoftTypography variant="caption" fontWeight="bold" color="text" sx={{ flex: "0 0 85px", textAlign: "right" }}>DUE</SoftTypography>
            <SoftTypography variant="caption" fontWeight="bold" color="text" sx={{ flex: "0 0 80px", textAlign: "center" }}>STATUS</SoftTypography>
            <SoftBox sx={{ flex: "0 0 75px" }}>
              <SoftTypography variant="caption" fontWeight="bold" color="text" sx={{ fontSize: "0.6rem", color: "#bbb" }}>
                ACTIONS
              </SoftTypography>
            </SoftBox>
          </SoftBox>

          {loading ? (
            <FootballLoader label="Loading fees…" />
          ) : fees.length === 0 ? (
            <SoftBox p={4} textAlign="center">
              <SoftTypography variant="caption" color="text">No fee records found for the selected filters</SoftTypography>
            </SoftBox>
          ) : (
            fees.map((f) => <FeeRow key={f.id} record={f} onPay={openPay} onViewHistory={openHistory} onEdit={openEdit} />)
          )}

          {/* Footer total row */}
          {fees.length > 0 && (
            <SoftBox display="flex" alignItems="center" px={2} py={1.5}
              sx={{ borderTop: "2px solid #f0f0f0", backgroundColor: "#fafafa" }}>
              <SoftTypography variant="caption" fontWeight="bold" color="dark" sx={{ flex: 1 }}>
                TOTAL ({fees.length} records)
              </SoftTypography>
              <SoftBox sx={{ flex: "0 0 120px" }} />
              <SoftTypography variant="caption" fontWeight="bold" color="dark" sx={{ flex: "0 0 85px", textAlign: "right" }}>
                ₹{totalFees.toLocaleString("en-IN")}
              </SoftTypography>
              <SoftTypography variant="caption" fontWeight="bold" sx={{ flex: "0 0 85px", textAlign: "right", color: "#2e7d32" }}>
                ₹{totalCollected.toLocaleString("en-IN")}
              </SoftTypography>
              <SoftTypography variant="caption" fontWeight="bold" sx={{ flex: "0 0 85px", textAlign: "right", color: totalPending > 0 ? "#d32f2f" : "#2e7d32" }}>
                ₹{totalPending.toLocaleString("en-IN")}
              </SoftTypography>
              <SoftBox sx={{ flex: "0 0 80px", textAlign: "center" }}>
                <SoftTypography variant="caption" fontWeight="bold" sx={{ color: "#1a73e8" }}>
                  {collectionPct}% collected
                </SoftTypography>
              </SoftBox>
              <SoftBox sx={{ flex: "0 0 75px" }} />
            </SoftBox>
          )}
        </Card>

      </SoftBox>
      <Footer company={{ href: "#", name: "Mohammedan SC" }} links={[]} />

      {/* ── Payment dialog ── */}
      <Dialog open={payDialog} onClose={() => setPayDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Record Payment</DialogTitle>
        <DialogContent>
          {payRecord && (
            <SoftBox mb={2} p={1.5} borderRadius="lg" sx={{ backgroundColor: "#f8f9fa" }}>
              <SoftTypography variant="caption" fontWeight="bold" color="dark">
                {payRecord.playerName}
              </SoftTypography>
              <SoftTypography variant="caption" color="text" display="block">
                {MONTHS[(payRecord.feeMonth ?? 1) - 1]} {payRecord.feeYear}
                {" · "}Outstanding: ₹{Number(payRecord.outstandingBalance ?? 0).toLocaleString("en-IN")}
              </SoftTypography>
            </SoftBox>
          )}
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField fullWidth required label="Amount (₹)" type="number" value={payForm.amount}
                onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })} size="small"
                InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }} />
            </Grid>
            <Grid item xs={6}>
              <TextField select fullWidth label="Payment Mode" value={payForm.paymentMode}
                onChange={(e) => setPayForm({ ...payForm, paymentMode: e.target.value })} size="small">
                {PAYMENT_MODES.map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Date" type="date" value={payForm.paymentDate}
                onChange={(e) => setPayForm({ ...payForm, paymentDate: e.target.value })}
                size="small" InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Transaction Reference" value={payForm.transactionRef}
                onChange={(e) => setPayForm({ ...payForm, transactionRef: e.target.value })} size="small" />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Notes" value={payForm.notes}
                onChange={(e) => setPayForm({ ...payForm, notes: e.target.value })}
                size="small" multiline rows={2} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <SoftButton variant="text" color="secondary" onClick={() => setPayDialog(false)}>Cancel</SoftButton>
          <SoftButton variant="gradient" color="success" onClick={handlePay} disabled={saving}>
            {saving ? "Saving…" : "Record Payment"}
          </SoftButton>
        </DialogActions>
      </Dialog>

      {/* ── Per-player history dialog ── */}
      <PlayerHistoryDialog
        open={historyDialog}
        onClose={() => setHistoryDialog(false)}
        player={historyPlayer}
        onEdit={(rec) => { setHistoryDialog(false); openEdit(rec); }}
      />

      {/* ── Edit fee record dialog ── */}
      <EditFeeDialog
        open={editDialog}
        onClose={() => setEditDialog(false)}
        record={editRecord}
        onSaved={() => { load(); setHistoryDialog(false); }}
      />
    </DashboardLayout>
  );
}

export default AcademyFees;
